
import React from 'react';
import { PlayerProfile } from '../types';
import { Droplets, Sword, User } from 'lucide-react';
import { UPGRADE_PRICES } from '../constants';
import { ExitButton } from './ExitButton';
import { Notification } from './NotificationSystem';

interface BathhouseProps {
  player: PlayerProfile;
  updatePlayer: (updates: Partial<PlayerProfile>) => void;
  onExit: () => void;
  onFight: () => void;
  onNotify: (message: string, type?: Notification['type']) => void;
}

export const Bathhouse: React.FC<BathhouseProps> = ({ player, updatePlayer, onExit, onFight, onNotify }) => {
  const allSpiritFull = player.group.members.every(m => m.spirit >= m.maxSpirit);

  const relax = () => {
      const COST = UPGRADE_PRICES.BATHHOUSE_ENTRY;
      if (player.cash >= COST) {
          if (allSpiritFull) {
              onNotify("The group is already fully rested!", 'info');
              return;
          }
          
          updatePlayer({
              cash: player.cash - COST,
              group: {
                ...player.group,
                members: player.group.members.map(m => ({ ...m, spirit: Math.min(m.maxSpirit, m.spirit + 15) }))
              }
          });
          onNotify("The steamy waters heal your spirit. (+15 Spirit for all)", 'success');
      } else {
          onNotify("Not enough cash for the entrance fee.", 'error');
      }
  };

  return (
    <div className="h-full w-full bg-slate-950 flex flex-col items-center justify-center p-8 relative">
       {/* Background: Teal Tiles */}
       <div className="absolute inset-0" style={{ 
           backgroundColor: '#115e59',
           backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
           backgroundSize: '40px 40px'
       }}></div>
       
       <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

       <ExitButton onExit={onExit} />

       <div className="relative z-10 w-full max-w-4xl glass-panel p-8 rounded-lg shadow-2xl border border-teal-500/30">
            <div className="text-center mb-8">
                <h2 className="text-4xl font-heading text-white mb-2 drop-shadow-md">TRADITIONAL BATHHOUSE</h2>
                <div className="text-teal-200 text-sm">"Relax... or investigate."</div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-base">
                
                {/* RELAX OPTION */}
                <div className="bg-teal-900/60 p-8 rounded-xl border border-teal-600 hover:border-teal-400 transition-colors flex flex-col items-center text-center">
                    <Droplets size={64} className="text-teal-300 mb-6 animate-bounce" />
                    <h3 className="text-2xl font-bold font-heading text-white mb-2">Soak in Hot Spring</h3>
                    <p className="text-teal-100 mb-6">Restore Spirit for the whole group. Cheaper than the cafe.</p>
                    <button 
                        onClick={relax}
                        disabled={player.cash < UPGRADE_PRICES.BATHHOUSE_ENTRY || allSpiritFull}
                        className="w-full py-4 bg-teal-700 border border-teal-500 hover:bg-teal-600 disabled:bg-slate-800 disabled:text-slate-500 disabled:border-slate-700 text-white font-bold rounded-lg transition-all shadow-lg"
                    >
                        {allSpiritFull ? 'SPIRIT FULL' : `ENTER ($${UPGRADE_PRICES.BATHHOUSE_ENTRY})`}
                    </button>
                </div>

                {/* FIGHT OPTION */}
                <div className="bg-red-900/60 p-8 rounded-xl border border-red-600 hover:border-red-400 transition-colors flex flex-col items-center text-center group">
                    <div className="relative">
                        <User size={64} className="text-red-300 mb-6 group-hover:scale-110 transition-transform" />
                        <Sword size={32} className="absolute -bottom-2 -right-2 text-yellow-400" />
                    </div>
                    <h3 className="text-2xl font-bold font-heading text-white mb-2">Confront Saja Boys</h3>
                    <p className="text-red-100 mb-6">"We found them hiding in the steam! Flush them out!"<br/> <span className="text-xs text-yellow-300">(Boss: Jinu & The Horde)</span></p>
                    <button 
                        onClick={onFight}
                        className="w-full py-4 bg-red-700 border border-red-500 hover:bg-red-600 text-white font-bold rounded-lg transition-all shadow-lg"
                    >
                        START RAID
                    </button>
                </div>
            </div>

            <div className="mt-8 p-4 bg-black/40 rounded-lg text-center border border-white/10">
                <p className="text-slate-300 text-sm italic">
                    Zoey: "I knew they were here! I can smell the cheap cologne through the sulfur!"
                </p>
            </div>
       </div>
    </div>
  );
};
